/**
 * TremorSenseSkillEffect — Earth tree shared trunk passive (earth_shared_passive_2).
 *
 * While the player has been stationary for at least 1000ms, all enemies within
 * 8 tiles register as directional radar pulses in the player's perception.
 * Heading and approximate distance only — not full visibility. Enemies behind
 * walls still register; LOS is NOT checked. The effect is purely a HUD/perception
 * layer with no impact on combat, A* pathfinding, or enemy behavior.
 *
 * Detection is computed on query (not cached). The caller (HUD layer or
 * PlayerStateComponent) calls `getDetectedEnemies(...)` each frame and renders
 * the directional indicators.
 *
 * This node is the Fork 1 gate — unlocking Tremor Sense presents the Fork 1
 * choice (stone_fist / wall / sculptor).
 *
 * UpgradeEffectResolver key(s):
 * - detectionRadiusTiles: value 8, scope tree 'earth', aggregation max
 * - stationaryRequirementMs: value 1000
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Nodes Array — Tremor Sense
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Enemy position passed to detection queries. */
export interface EnemyEntry {
  readonly id: string;
  readonly distanceTiles: number;
}

/** A detected enemy directional entry returned to the HUD. */
export interface TremorSenseDetection {
  readonly id: string;
  readonly distanceTiles: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TremorSenseDeps {
  readonly stationary: {
    isStationary(thresholdMs: number): boolean;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TremorSenseConfig {
  /** Detection radius in tiles. */
  readonly detectionRadiusTiles: number;
  /** Minimum stationary duration (ms) before detection activates. */
  readonly stationaryRequirementMs: number;
}

const DEFAULT_CONFIG: TremorSenseConfig = {
  detectionRadiusTiles: 8,
  stationaryRequirementMs: 1000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TremorSenseSkillEffect implements SkillEffect {
  readonly skillId = 'earth_shared_passive_2';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: TremorSenseDeps;
  private readonly config: TremorSenseConfig;

  constructor(deps: TremorSenseDeps, config: Partial<TremorSenseConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Resolved value helpers ---

  /** Resolve detection radius from the upgrade resolver, falling back to config default. */
  private resolveDetectionRadiusTiles(): number {
    const resolved = this.deps.resolver.getAdditiveEffect('earth_shared_passive_2', 'detectionRadiusTiles');
    return resolved !== 0 ? resolved : this.config.detectionRadiusTiles;
  }

  /** Resolve stationary requirement from the upgrade resolver, falling back to config default. */
  private resolveStationaryRequirementMs(): number {
    const resolved = this.deps.resolver.getAdditiveEffect('earth_shared_passive_2', 'stationaryRequirementMs');
    return resolved !== 0 ? resolved : this.config.stationaryRequirementMs;
  }

  // --- Config getters ---

  get detectionRadiusTiles(): number { return this.resolveDetectionRadiusTiles(); }
  get stationaryRequirementMs(): number { return this.resolveStationaryRequirementMs(); }

  // --- Detection query interface ---

  /**
   * Whether the stationary condition is currently met — i.e., whether the
   * HUD should show directional indicators at all.
   */
  get isDetecting(): boolean {
    return this.deps.stationary.isStationary(this.resolveStationaryRequirementMs());
  }

  /**
   * Returns all enemies within the detection radius when the stationary
   * condition is met; returns an empty array when inactive (not yet stationary
   * long enough, or player moving).
   *
   * The caller passes all known enemy entries; TremorSense filters them by
   * radius and stationary state. No LOS check is performed.
   *
   * @param enemies - All enemies to consider, with their current distance in tiles.
   * @returns Detected enemies (within radius) when stationary; [] otherwise.
   */
  getDetectedEnemies(enemies: readonly EnemyEntry[]): TremorSenseDetection[] {
    if (!this.isDetecting) return [];

    const radius = this.resolveDetectionRadiusTiles();
    return enemies
      .filter(e => e.distanceTiles <= radius)
      .map(e => ({ id: e.id, distanceTiles: e.distanceTiles }));
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — stationary state is owned by StationaryTracker
  }

  destroy(): void {
    // No owned resources
  }
}
